import { useEffect } from 'react';
import { Outlet, Link, NavLink, useParams, useNavigate, Navigate } from 'react-router-dom';
import { BotWorkspaceProvider, useBotWorkspace } from '../../hooks/useBotWorkspace';
import { useDashboardChrome } from '../../hooks/useDashboardChrome';
import { useIsMobile } from '../../hooks/useIsMobile';
import { botIcon, botLabel, statusColor, statusLabel, type Bot } from '../../utils/botHelpers';

const TAB_CONFIG: Record<string, Array<{ id: string; icon: string; label: string }>> = {
  whatsapp: [
    { id: 'overview',      icon: '📊', label: 'Overview' },
    { id: 'conversations', icon: '💬', label: 'Conversations' },
    { id: 'users',         icon: '👥', label: 'Users' },
    { id: 'orders',        icon: '🛒', label: 'Orders' },
    { id: 'bookings',      icon: '📅', label: 'Bookings' },
    { id: 'reports',       icon: '📈', label: 'Reports' },
    { id: 'insights',      icon: '🧠', label: 'Insights' },
  ],
  facebook: [
    { id: 'overview',      icon: '📊', label: 'Overview' },
    { id: 'conversations', icon: '💬', label: 'Messages' },
    { id: 'posts',         icon: '📝', label: 'Posts' },
    { id: 'users',         icon: '👥', label: 'Users' },
    { id: 'orders',        icon: '🛒', label: 'Orders' },
    { id: 'bookings',      icon: '📅', label: 'Bookings' },
    { id: 'reports',       icon: '📈', label: 'Reports' },
    { id: 'insights',      icon: '🧠', label: 'Insights' },
  ],
  facebook_comments: [
    { id: 'overview',      icon: '📊', label: 'Overview' },
    { id: 'comments',      icon: '📝', label: 'Comments' },
    { id: 'insights',      icon: '🧠', label: 'Insights' },
  ],
  website: [
    { id: 'overview',      icon: '📊', label: 'Overview' },
    { id: 'conversations', icon: '💬', label: 'Chats' },
    { id: 'users',         icon: '👥', label: 'Users' },
    { id: 'orders',        icon: '🛒', label: 'Orders' },
    { id: 'bookings',      icon: '📅', label: 'Bookings' },
    { id: 'embed',         icon: '🔌', label: 'Embed code' },
    { id: 'insights',      icon: '🧠', label: 'Insights' },
  ],
  voice: [
    { id: 'overview',      icon: '📊', label: 'Overview' },
    { id: 'conversations', icon: '🎙️', label: 'Call logs' },
    { id: 'users',         icon: '👥', label: 'Users' },
  ],
  ai: [
    { id: 'overview',      icon: '📊', label: 'Overview' },
    { id: 'conversations', icon: '💬', label: 'Conversations' },
    { id: 'users',         icon: '👥', label: 'Users' },
    { id: 'orders',        icon: '🛒', label: 'Orders' },
    { id: 'bookings',      icon: '📅', label: 'Bookings' },
    { id: 'reports',       icon: '📈', label: 'Reports' },
    { id: 'insights',      icon: '🧠', label: 'Insights' },
  ],
  email: [
    { id: 'overview',      icon: '📧', label: 'Inbox' },
    { id: 'users',         icon: '👥', label: 'Contacts' },
    { id: 'orders',        icon: '🛒', label: 'Orders' },
    { id: 'reports',       icon: '📈', label: 'Reports' },
    { id: 'insights',      icon: '🧠', label: 'Insights' },
  ],
};

export default function BotLayout() {
  const { botId } = useParams<{ botId: string }>();
  if (!botId) return <Navigate to="/dashboard" replace />;
  return (
    <BotWorkspaceProvider botId={botId}>
      <BotLayoutInner />
    </BotWorkspaceProvider>
  );
}

function ActionButtons({ type, bot, userData, openSettings }: { type: string; bot: Bot; userData: any; openSettings: (id: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      {type === 'voice' ? (
        <Link to={`/builder/voice/${bot.id}`} style={{ background: 'linear-gradient(135deg, #5B4FE8,#7c3aed)', color: 'white', padding: '8px 18px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>🎙️ Edit script</Link>
      ) : type === 'website' ? (
        <Link to={`/builder/website/${bot.id}`} style={{ background: 'linear-gradient(135deg, #5B4FE8,#7c3aed)', color: 'white', padding: '8px 18px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>🌐 Edit FAQs & widget</Link>
      ) : type === 'facebook' ? (
        <Link to={`/builder/facebook/${bot.id}`} style={{ background: 'linear-gradient(135deg, #5B4FE8,#7c3aed)', color: 'white', padding: '8px 18px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>📘 Edit replies</Link>
      ) : type === 'ai' ? (
        <button onClick={() => openSettings(bot.id)} style={{ background: 'linear-gradient(135deg, #5B4FE8,#7c3aed)', color: 'white', padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>🤖 Edit knowledge base</button>
      ) : type === 'facebook_comments' ? (
        // AI-only, same as the ai type above - there's no flow-builder path
        // for comment replies, so this opens the same knowledge-base/AI
        // config modal rather than linking to the FacebookBuilder flow UI.
        <button onClick={() => openSettings(bot.id)} style={{ background: 'linear-gradient(135deg, #5B4FE8,#7c3aed)', color: 'white', padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>📝 Edit AI reply settings</button>
      ) : type === 'email' ? null /* Overview's own Inbox panel already has a refresh control */ : (
        <>
          <Link to={`/builder/whatsapp/${bot.id}`} style={{ background: 'linear-gradient(135deg, #5B4FE8,#7c3aed)', color: 'white', padding: '8px 18px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>💬 Edit flow</Link>
          {userData?.plan && userData.plan !== 'free' ? (
            <Link to={`/campaigns/${bot.id}`} style={{ background: 'linear-gradient(135deg, #f59e0b,#d97706)', color: 'white', padding: '8px 18px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>📢 Campaigns</Link>
          ) : (
            <Link to="/billing" title="Campaigns require a paid plan" style={{ background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)', padding: '8px 18px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>📢 Campaigns 🔒</Link>
          )}
        </>
      )}
      <button onClick={() => openSettings(bot.id)} style={{ background: 'var(--bg-element)', color: 'var(--text-secondary)', padding: '8px 16px', border: '1px solid var(--card-border)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
        Settings
      </button>
    </div>
  );
}

function BotLayoutInner() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { bot, notFound, loading } = useBotWorkspace();
  const { userData, openSettings } = useDashboardChrome();

  // Deep links to a bot workspace don't make sense on the mobile layout
  // (DashboardMobile never mounts an <Outlet/> to render these routes).
  useEffect(() => { if (isMobile) navigate('/dashboard', { replace: true }); }, [isMobile, navigate]);

  if (loading) {
    return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>Loading…</div>;
  }
  if (notFound || !bot) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: '600' }}>Bot not found.</div>
        <Link to="/dashboard" style={{ color: '#818cf8', fontSize: '13px' }}>← Back to dashboard</Link>
      </div>
    );
  }

  const isPending = bot.status === 'pending_verification' || bot.status === 'pending_setup';
  // Comments-mode shares botType: 'facebook' with the messenger bots - this
  // local display-only type string is what the tabs/action-button branches
  // key off, not bot.botType directly.
  const type = bot.botType === 'facebook' && bot.fbMode === 'comments' ? 'facebook_comments' : (bot.botType || 'whatsapp');
  const tabs = TAB_CONFIG[type] || TAB_CONFIG.whatsapp;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-page)', minWidth: 0 }}>
      <div style={{ background: 'var(--bg-card)', padding: '16px 24px', borderBottom: '1px solid var(--bg-element)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>{botIcon(bot)}</span>
              <h1 style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '700', margin: 0 }}>{bot.businessName}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '3px', background: statusColor(bot.status) }} />
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{statusLabel(bot.status)}</span>
              </div>
            </div>
            <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginTop: '3px', marginLeft: '30px' }}>
              {botLabel(bot)} bot
              {bot.phoneNumber && ` · ${bot.phoneNumber}`}
              {bot.websiteUrl && ` · ${bot.websiteUrl}`}
              {bot.fbPageId && ` · Page: ${bot.fbPageId}`}
            </div>
          </div>
          <ActionButtons type={type} bot={bot} userData={userData} openSettings={openSettings} />
        </div>

        {isPending && (
          <div style={{ background: '#f59e0b11', border: '1px solid #f59e0b30', borderRadius: '9px', padding: '10px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' }}>
            <span style={{ fontSize: '16px' }}>⏳</span>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              <span style={{ color: '#f59e0b', fontWeight: '600' }}>Setup in progress. </span>
              {bot.status === 'pending_verification'
                ? "Your WhatsApp number is being registered with Meta. Usually 1–3 business days. We'll email you when it's live."
                : "Our team is setting up your bot. We'll send you everything by email when it's ready."}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '4px' }}>
          {tabs.map(tab => (
            <NavLink key={tab.id} to={tab.id}
              style={({ isActive }) => ({
                padding: '7px 16px', border: 'none', borderRadius: '7px', cursor: 'pointer', textDecoration: 'none',
                background: isActive ? '#5B4FE8' : 'var(--bg-element)',
                color: isActive ? 'white' : 'var(--text-muted)',
                fontSize: '12px', fontWeight: isActive ? 600 : 400,
              })}>
              {tab.icon} {tab.label}
            </NavLink>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        <Outlet />
      </div>
    </div>
  );
}
